import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { 
  Upload, FileText, File, Trash2, Download, 
  FileCheck, FileSpreadsheet, Ship, ChevronDown, Edit2, Check, X,
  Eye, Share2, MessageCircle, Send, Copy, Briefcase, Link2, ExternalLink, Globe, Landmark, Camera, Anchor, Receipt, Mail, Warehouse, Archive, Flag, Mic, Play
} from "lucide-react";
import { toast } from "sonner";
import ScanModal from "@/pages/trade/ScanModal";
import AudioRecordModal from "@/pages/trade/AudioRecordModal";

const DOC_TYPES = [
  { value: "invoice", label: "Invoice", icon: FileText },
  { value: "bill_of_lading", label: "Bill of Lading", icon: FileCheck },
  { value: "customs", label: "Customs Form", icon: FileSpreadsheet },
  { value: "packing_list", label: "Packing List", icon: File },
  { value: "receipt", label: "Receipt", icon: FileText },
  { value: "audio_note", label: "Audio Note", icon: Mic },
  { value: "other", label: "Other", icon: File },
];

const COMPANY_DOC_TYPES = [
  { value: "business_license", label: "Business License", icon: FileCheck },
  { value: "tin_certificate", label: "TIN Certificate", icon: FileText },
  { value: "tax_registration", label: "Tax Registration", icon: FileSpreadsheet },
  { value: "permit", label: "Permit", icon: File },
  { value: "contract", label: "Contract", icon: FileText },
  { value: "audio_note", label: "Audio Note", icon: Mic },
  { value: "other", label: "Other", icon: File },
];

const LINK_GROUPS = [
  {
    title: "License & Renewal",
    links: [
      { id: "etrade", name: "eTrade — License Renewal", url: "https://etrade.gov.et", desc: "Renew or register your trade license online (official OTRLS portal)", icon: FileCheck },
      { id: "motri", name: "Ministry of Trade (MoTRI)", url: "https://www.motri.gov.et", desc: "Trade license services, requirements and guides", icon: Landmark },
      { id: "eservices", name: "eServices Ethiopia", url: "https://www.eservices.gov.et", desc: "All Ethiopian government e-services in one place", icon: Globe },
      { id: "national-id-update", name: "Ethiopian ID Update", url: "https://id.gov.et/update?utm_source=chatgpt.com", desc: "Update or verify Ethiopian national ID information online", icon: FileCheck },
    ],
  },
  {
    title: "Customs & Import",
    links: [
      { id: "esw", name: "Ethiopian Single Window (ESW)", url: "https://esw.et", desc: "Import/export declarations and LPCO permits in one official portal", icon: FileText },
      { id: "ecc", name: "Ethiopian Customs Commission", url: "https://ecc.gov.et", desc: "Customs information, tariffs and trader guidance", icon: Ship },
      { id: "customs-portal", name: "Ethiopian Customs Trade Portal", url: "https://customs.erca.gov.et", desc: "Check HS code, customs procedure and required import documents", icon: FileSpreadsheet },
      { id: "ethiopia-business-portal", name: "Ethiopia Business Portal", url: "https://new.business.gov.et", desc: "Official business and import process guidance", icon: Briefcase },
      { id: "nbe", name: "National Bank of Ethiopia", url: "https://nbe.gov.et", desc: "Foreign exchange directives and bank permit rules", icon: Landmark },
    ],
  },
  {
    title: "Ports & Logistics",
    links: [
      { id: "dpcs", name: "Djibouti Port Community (DPCS)", url: "https://www.dpcs.dj", desc: "Port fees, cargo tracking, delivery orders and trade documents", icon: Anchor },
    ],
  },
];

const DocumentsPage = () => {
  const { API, authHeader, token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("shipping");
  const [docType, setDocType] = useState("invoice");
  const [description, setDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [selectedShipmentId, setSelectedShipmentId] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [editingDoc, setEditingDoc] = useState(null);
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState("");
  const [editShipment, setEditShipment] = useState("");
  const [editExpiry, setEditExpiry] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [playingDocId, setPlayingDocId] = useState(null);
  const [shareDocId, setShareDocId] = useState(null);
  const [accountant, setAccountant] = useState(null);
  const [accDocId, setAccDocId] = useState(null);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();
  const [scanOpen, setScanOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [shipViewFilter, setShipViewFilter] = useState("all");

  const handleScanPdf = async (file) => {
    setScanOpen(false);
    await uploadFile(file);
  };

  const handleAudioSave = async (file, name) => {
    setRecordOpen(false);
    await uploadFile(file, { docType: "audio_note", description: name });
  };

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/trade/documents`, { headers: authHeader() }),
      axios.get(`${API}/trade/shipments`, { headers: authHeader() }),
      axios.get(`${API}/trade/contacts?type=accountant`, { headers: authHeader() })
    ])
      .then(([d, s, c]) => {
        setDocs(d.data);
        setShipments(s.data);
        setAccountant(c.data[0] || null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // "roof" = Goldspot Roof Sheeting company files, "misc" = miscellaneous, "usa" = USA file cabinet — all behave like the company cabinet
  const isCabinet = ["company", "roof", "misc", "usa"].includes(category);
  const activeDocTypes = isCabinet ? COMPANY_DOC_TYPES : DOC_TYPES;
  const categoryDocs = useMemo(
    () => docs.filter(d => (d.category || "shipping") === category),
    [docs, category]
  );

  const switchCategory = (c) => {
    setCategory(c);
    if (["company", "roof", "misc", "usa"].includes(c)) setDocType("business_license");
    else if (c === "shipping") setDocType("invoice");
    setSelectedShipmentId("");
    setExpiryDate("");
    setShareDocId(null);
  };

  // Group shipping documents by shipment_id
  const grouped = useMemo(() => {
    const g = {};
    categoryDocs.forEach(d => {
      const key = d.shipment_id || "_unassigned";
      if (!g[key]) g[key] = [];
      g[key].push(d);
    });
    return g;
  }, [categoryDocs]);

  const shipmentById = useMemo(() => {
    const m = {};
    shipments.forEach(s => { m[s.id] = s; });
    return m;
  }, [shipments]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const uploadFile = async (file, overrides = {}) => {
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", overrides.docType ?? docType);
    formData.append("description", overrides.description ?? description);
    formData.append("category", category);
    formData.append("shipment_id", category === "shipping" ? selectedShipmentId : "");
    if (isCabinet) formData.append("expiry_date", expiryDate);

    try {
      await axios.post(
        `${API}/trade/documents/upload`,
        formData,
        { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${file.name} uploaded`);
      setDescription("");
      setExpiryDate("");
      fetchData();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/trade/documents/${docId}`, { headers: authHeader() });
      toast.success("Document deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const startEdit = (doc) => {
    setEditingDoc(doc.id);
    setEditName(doc.original_name);
    setEditType(doc.doc_type);
    setEditShipment(doc.shipment_id || "_none");
    setEditExpiry(doc.expiry_date || "");
  };

  const cancelEdit = () => {
    setEditingDoc(null);
    setEditName("");
    setEditType("");
    setEditShipment("");
    setEditExpiry("");
  };

  const saveEdit = async (doc) => {
    try {
      const payload = {
        original_name: editName.trim(),
        doc_type: editType,
        shipment_id: editShipment === "_none" ? "" : editShipment,
      };
      if (["company", "roof", "misc", "usa"].includes(doc.category)) {
        payload.expiry_date = editExpiry;
      }
      await axios.put(`${API}/trade/documents/${doc.id}`, payload, { headers: authHeader() });
      toast.success("Document updated");
      cancelEdit();
      fetchData();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/trade/documents/${doc.id}/download?auth=${token}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  // Preview helpers
  const previewUrl = (doc) => `${API}/trade/documents/${doc.id}/preview?auth=${token}`;
  const isImage = (doc) =>
    (doc.content_type || "").startsWith("image/") || /\.(jpg|jpeg|png|webp|gif)$/i.test(doc.original_name);
  const isPdf = (doc) =>
    (doc.content_type || "").includes("pdf") || /\.pdf$/i.test(doc.original_name);
  const isAudio = (doc) =>
    doc.doc_type === "audio_note" || (doc.content_type || "").startsWith("audio/") || /\.(webm|wav|mp3|m4a|ogg)$/i.test(doc.original_name);

  // Expiry helpers
  const expiryInfo = (doc) => {
    if (!doc.expiry_date) return null;
    const days = Math.ceil((new Date(doc.expiry_date) - Date.now()) / 86400000);
    if (days < 0) return { label: `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`, cls: "text-red-400" };
    if (days <= 30) return { label: `Expires in ${days} day${days === 1 ? "" : "s"}`, cls: "text-amber-400" };
    return { label: `Valid until ${new Date(doc.expiry_date).toLocaleDateString()}`, cls: "trade-green" };
  };

  // Share helpers (public share link — no login needed to open it)
  const shareUrl = (doc) => `${API}/trade/documents/shared/${doc.share_id}`;
  const shareWhatsApp = (doc) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${doc.original_name}\n${shareUrl(doc)}`)}`, "_blank");
  };
  const shareTelegram = (doc) => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl(doc))}&text=${encodeURIComponent(doc.original_name)}`, "_blank");
  };
  const copyShareLink = async (doc) => {
    try {
      await navigator.clipboard.writeText(shareUrl(doc));
      toast.success("Share link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  const deviceShare = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/trade/documents/${doc.id}/download?auth=${token}`,
        { responseType: "blob" }
      );
      const file = new File([response.data], doc.original_name, {
        type: doc.content_type || "application/octet-stream"
      });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: doc.original_name });
      } else if (navigator.share) {
        await navigator.share({ title: doc.original_name, url: shareUrl(doc) });
      } else {
        copyShareLink(doc);
      }
    } catch {}
  };

  const getIcon = (type) => {
    const t = [...DOC_TYPES, ...COMPANY_DOC_TYPES].find(d => d.value === type);
    return t ? t.icon : File;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
  };

  const renderDocCard = (doc, i) => {
    const Icon = getIcon(doc.doc_type);
    const isEditing = editingDoc === doc.id;
    const isCompanyDoc = (doc.category || "shipping") !== "shipping";
    const expiry = expiryInfo(doc);
    return (
      <motion.div
        key={doc.id}
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ delay: i * 0.03 }}
        className="trade-card rounded-sm p-5"
        data-testid={`doc-card-${doc.id}`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
            <Icon className="w-6 h-6 trade-gold" strokeWidth={1.5} />
          </div>
          {!isEditing && (
            <span className="text-xs uppercase tracking-widest text-white/40">
              {doc.doc_type.replace(/_/g, " ")}
            </span>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-white/40">Name</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} className="trade-input h-9 rounded-sm mt-1 text-xs" data-testid={`edit-name-${doc.id}`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-white/40">Type</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger className="trade-input h-9 rounded-sm mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#142039] border-white/10">
                  {(isCompanyDoc ? COMPANY_DOC_TYPES : DOC_TYPES).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isCompanyDoc && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Expiry Date</Label>
                <Input type="date" value={editExpiry} onChange={e => setEditExpiry(e.target.value)} className="trade-input h-9 rounded-sm mt-1 text-xs" data-testid={`edit-expiry-${doc.id}`} />
              </div>
            )}
            {!isCompanyDoc && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Shipment</Label>
                <Select value={editShipment} onValueChange={setEditShipment}>
                  <SelectTrigger className="trade-input h-9 rounded-sm mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#142039] border-white/10 max-h-56">
                    <SelectItem value="_none">Unassigned</SelectItem>
                    {shipments.map(s => <SelectItem key={s.id} value={s.id}>{s.shipment_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => saveEdit(doc)}
                className="flex-1 text-xs trade-green hover:bg-[#10b981]/10 py-2 rounded-sm flex items-center justify-center gap-1 border border-[#10b981]/30"
                data-testid={`save-edit-${doc.id}`}
              >
                <Check size={12} /> Save
              </button>
              <button
                onClick={cancelEdit}
                className="text-xs text-white/60 hover:bg-white/5 px-3 py-2 rounded-sm border border-white/10"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ) : (
          <>
            <h4 className="font-semibold text-sm mb-1 truncate" title={doc.original_name}>
              {doc.original_name}
            </h4>
            {doc.description && (
              <p className="text-xs text-white/50 mb-3">{doc.description}</p>
            )}
            <p className="text-xs text-white/40">
              {formatSize(doc.file_size)} · {new Date(doc.uploaded_at).toLocaleDateString()}
            </p>
            {expiry && (
              <p className={`text-[11px] mt-1 font-semibold ${expiry.cls}`} data-testid={`expiry-badge-${doc.id}`}>
                {expiry.label}
              </p>
            )}
            <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
              {isAudio(doc) ? (
                <button
                  onClick={() => setPlayingDocId(playingDocId === doc.id ? null : doc.id)}
                  className="flex-1 text-xs trade-green hover:bg-[#10b981]/10 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                  data-testid={`play-doc-${doc.id}`}
                >
                  <Play size={12} /> {playingDocId === doc.id ? "Stop" : "Play"}
                </button>
              ) : (
                <button
                  onClick={() => setPreviewDoc(doc)}
                  className="flex-1 text-xs trade-green hover:bg-[#10b981]/10 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                  data-testid={`preview-btn-${doc.id}`}
                >
                  <Eye size={12} /> Preview
                </button>
              )}
              <button
                onClick={() => setShareDocId(shareDocId === doc.id ? null : doc.id)}
                className={`text-xs px-3 py-2 rounded-sm transition-colors ${shareDocId === doc.id ? "trade-gold bg-[#D4AF37]/10" : "text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
                data-testid={`share-btn-${doc.id}`}
                title="Share"
              >
                <Share2 size={12} />
              </button>
              <button
                onClick={() => handleDownload(doc)}
                className="text-xs text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-2 rounded-sm transition-colors"
                data-testid={`download-btn-${doc.id}`}
                title="Download"
              >
                <Download size={12} />
              </button>
              <button
                onClick={() => startEdit(doc)}
                className="text-xs text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-2 rounded-sm transition-colors"
                data-testid={`edit-btn-${doc.id}`}
                title="Edit"
              >
                <Edit2 size={12} />
              </button>
              <button
                onClick={() => handleDelete(doc.id)}
                className="text-xs text-red-400 hover:bg-red-500/10 px-3 py-2 rounded-sm transition-colors"
              >
                <Trash2 size={12} />
              </button>
            </div>
            {isAudio(doc) && playingDocId === doc.id && (
              <div className="mt-3" data-testid={`doc-player-${doc.id}`}>
                <audio controls autoPlay src={previewUrl(doc)} className="w-full h-9" />
              </div>
            )}
            {shareDocId === doc.id && (
              <div className="flex gap-2 mt-2" data-testid={`share-options-${doc.id}`}>
                <button
                  onClick={() => shareWhatsApp(doc)}
                  className="flex-1 text-xs trade-green hover:bg-[#10b981]/10 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                  data-testid={`share-whatsapp-${doc.id}`}
                >
                  <MessageCircle size={12} /> WhatsApp
                </button>
                <button
                  onClick={() => shareTelegram(doc)}
                  className="flex-1 text-xs text-[#24A1DE] hover:bg-[#24A1DE]/10 py-2 rounded-sm flex items-center justify-center gap-1 transition-colors"
                  data-testid={`share-telegram-${doc.id}`}
                >
                  <Send size={12} /> Telegram
                </button>
                <button
                  onClick={() => deviceShare(doc)}
                  className="text-xs text-white/60 hover:text-white hover:bg-white/5 px-3 py-2 rounded-sm transition-colors"
                  data-testid={`share-device-${doc.id}`}
                  title="Share via device apps"
                >
                  <Share2 size={12} />
                </button>
                <button
                  onClick={() => copyShareLink(doc)}
                  className="text-xs text-white/60 hover:text-white hover:bg-white/5 px-3 py-2 rounded-sm transition-colors"
                  data-testid={`share-copy-${doc.id}`}
                  title="Copy share link"
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={() => {
                    if (!accountant) {
                      toast.error("Add your accountant in the Accountant tab first");
                      return;
                    }
                    setAccDocId(accDocId === doc.id ? null : doc.id);
                  }}
                  className={`flex-1 text-xs py-2 rounded-sm flex items-center justify-center gap-1 transition-colors ${
                    accDocId === doc.id ? "trade-gold bg-[#D4AF37]/10" : "text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  }`}
                  data-testid={`share-accountant-${doc.id}`}
                  title="Send to accountant"
                >
                  <Receipt size={12} /> Accountant
                </button>
              </div>
            )}
            {shareDocId === doc.id && accDocId === doc.id && accountant && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-white/5 rounded-sm" data-testid={`acc-options-${doc.id}`}>
                <span className="text-[11px] text-white/50 truncate flex-1">To: {accountant.name}</span>
                {(accountant.whatsapp || accountant.phone) && (
                  <a
                    href={`https://wa.me/${(accountant.whatsapp || accountant.phone).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`${doc.original_name} — Goldspot Trade document\n${shareUrl(doc)}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                    data-testid={`acc-whatsapp-${doc.id}`}
                  >
                    <MessageCircle size={12} /> WhatsApp
                  </a>
                )}
                {accountant.email && (
                  <a
                    href={`mailto:${accountant.email}?subject=${encodeURIComponent(`Tax Filing Document — ${doc.original_name}`)}&body=${encodeURIComponent(`Please find the document here:\n${shareUrl(doc)}`)}`}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    data-testid={`acc-email-${doc.id}`}
                  >
                    <Mail size={12} /> Email
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </motion.div>
    );
  };

  // Order groups: shipments first (by shipment creation order), then unassigned at end
  const allGroupKeys = [
    ...shipments.filter(s => grouped[s.id]).map(s => s.id),
    ...(grouped._unassigned ? ["_unassigned"] : [])
  ];
  // Shipment filter: show only the selected shipment's files when picked
  const groupKeys = shipViewFilter === "all"
    ? allGroupKeys
    : allGroupKeys.filter(k => k === shipViewFilter);

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Documents</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">
            {category === "links" ? "Renewal & Help Links" : category === "company" ? "Company File Cabinet" : category === "roof" ? "Goldspot Roof Sheeting Files" : category === "misc" ? "Miscellaneous Files" : category === "usa" ? "USA File Cabinet" : "Shipping Documents"}
          </h1>
          <p className="text-white/50 mt-2">
            {category === "links"
              ? "License renewal, customs and banking portals"
              : category === "company"
              ? "Business license, certificates and company paperwork"
              : category === "roof"
              ? "Goldspot Roof Sheeting company license, certificates and paperwork"
              : category === "misc"
              ? "Anything that doesn't fit the other cabinets — receipts, personal scans, misc paperwork"
              : category === "usa"
              ? "US documents — tax records, IDs, personal and business paperwork"
              : "Organized by shipment — invoices, B/Ls, customs paperwork"}
          </p>
        </motion.div>

        {/* Category tabs */}
        <div className="grid grid-cols-3 gap-2 mb-8 md:flex md:flex-wrap">
          <button
            onClick={() => switchCategory("shipping")}
            className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2.5 text-[10px] leading-tight text-center md:text-xs rounded-sm transition-all ${
              category === "shipping"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="tab-shipping"
          >
            <Ship size={14} /> Shipping Documents
          </button>
          <button
            onClick={() => switchCategory("company")}
            className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2.5 text-[10px] leading-tight text-center md:text-xs rounded-sm transition-all ${
              category === "company"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="tab-company"
          >
            <Briefcase size={14} /> Company File Cabinet
          </button>
          <button
            onClick={() => switchCategory("roof")}
            className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2.5 text-[10px] leading-tight text-center md:text-xs rounded-sm transition-all ${
              category === "roof"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="tab-roof"
          >
            <Warehouse size={14} /> Goldspot Roof Sheeting
          </button>
          <button
            onClick={() => switchCategory("misc")}
            className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2.5 text-[10px] leading-tight text-center md:text-xs rounded-sm transition-all ${
              category === "misc"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="tab-misc"
          >
            <Archive size={14} /> Miscellaneous
          </button>
          <button
            onClick={() => switchCategory("usa")}
            className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2.5 text-[10px] leading-tight text-center md:text-xs rounded-sm transition-all ${
              category === "usa"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="tab-usa"
          >
            <Flag size={14} /> USA File Cabinet
          </button>
          <button
            onClick={() => switchCategory("links")}
            className={`flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2.5 text-[10px] leading-tight text-center md:text-xs rounded-sm transition-all ${
              category === "links"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="tab-links"
          >
            <Link2 size={14} /> Renewal & Help Links
          </button>
        </div>

        {/* Upload */}
        {category !== "links" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-8"
        >
          <h3 className="text-sm uppercase tracking-widest trade-gold mb-5">
            {category === "company" ? "Upload Company Document" : category === "roof" ? "Upload Roof Sheeting Document" : category === "misc" ? "Upload Miscellaneous Document" : category === "usa" ? "Upload USA Document" : "Upload Document"}
          </h3>
          <div className={`grid grid-cols-1 ${category === "company" ? "md:grid-cols-4" : "md:grid-cols-4"} gap-4 items-end`}>
            {category === "shipping" && (
              <div>
                <Label className="text-xs text-white/60">Shipment</Label>
                <Select value={selectedShipmentId || "_none"} onValueChange={v => setSelectedShipmentId(v === "_none" ? "" : v)}>
                  <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="upload-shipment-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#142039] border-white/10 max-h-72">
                    <SelectItem value="_none">Unassigned</SelectItem>
                    {shipments.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.shipment_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-white/60">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="upload-doctype-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#142039] border-white/10">
                  {activeDocTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isCabinet && (
              <div>
                <Label className="text-xs text-white/60">Expiry Date (optional)</Label>
                <Input
                  type="date"
                  value={expiryDate}
                  onChange={e => setExpiryDate(e.target.value)}
                  className="trade-input mt-1 h-11 rounded-sm"
                  data-testid="expiry-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs text-white/60">Description</Label>
              <Input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional note"
                className="trade-input mt-1 h-11 rounded-sm"
              />
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="camera-input"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 min-w-[90px] trade-btn-primary rounded-sm h-11"
                  data-testid="upload-doc-btn"
                >
                  <Upload size={16} className="mr-2" />
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
                <Button
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 min-w-[110px] bg-white/10 text-white hover:bg-white/20 rounded-sm h-11"
                  data-testid="camera-btn"
                >
                  <Camera size={16} className="mr-2" />
                  Take Photo
                </Button>
                <Button
                  onClick={() => setScanOpen(true)}
                  disabled={uploading}
                  className="flex-1 min-w-[90px] bg-white/10 text-white hover:bg-white/20 rounded-sm h-11"
                  data-testid="scan-btn"
                >
                  <FileText size={16} className="mr-2" />
                  Scan
                </Button>
                <Button
                  onClick={() => setRecordOpen(true)}
                  disabled={uploading}
                  className="flex-1 min-w-[90px] bg-white/10 text-white hover:bg-white/20 rounded-sm h-11"
                  data-testid="record-btn"
                >
                  <Mic size={16} className="mr-2" />
                  Record
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* Documents */}
        {category === "shipping" && !loading && categoryDocs.length > 0 && (
          <div className="flex items-center gap-3 mb-6" data-testid="ship-view-filter-row">
            <Label className="text-xs text-white/60 whitespace-nowrap">View shipment:</Label>
            <Select value={shipViewFilter} onValueChange={setShipViewFilter}>
              <SelectTrigger className="trade-input h-10 rounded-sm max-w-xs" data-testid="ship-view-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#142039] border-white/10 max-h-72">
                <SelectItem value="all" data-testid="ship-view-all">All Shipments</SelectItem>
                {shipments.filter(s => grouped[s.id]).map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`ship-view-${s.id}`}>{s.shipment_name}</SelectItem>
                ))}
                {grouped._unassigned && <SelectItem value="_unassigned">Unassigned</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        )}
        {category === "links" ? (
          <div className="space-y-10" data-testid="links-section">
            {LINK_GROUPS.map(group => (
              <div key={group.title}>
                <h3 className="text-sm uppercase tracking-widest trade-gold mb-4">{group.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.links.map(l => (
                    <a
                      key={l.id}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="trade-card rounded-sm p-5 block group hover:border-[#D4AF37]/30 transition-colors"
                      data-testid={`link-${l.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
                          <l.icon className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                        </div>
                        <ExternalLink size={14} className="text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                      </div>
                      <h4 className="font-semibold text-sm mb-1">{l.name}</h4>
                      <p className="text-xs text-white/50">{l.desc}</p>
                      <p className="text-[11px] trade-gold mt-2 font-mono">{l.url.replace("https://", "")}</p>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : loading ? (
          <div className="text-center p-16 text-white/40">Loading...</div>
        ) : categoryDocs.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            {isCabinet ? (
              <Briefcase className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            ) : (
              <FileText className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            )}
            <h3 className="text-xl font-serif mb-2">
              {isCabinet ? "No company documents yet" : "No documents yet"}
            </h3>
            <p className="text-white/50">
              {category === "roof"
                ? "Upload Goldspot Roof Sheeting license and company paperwork above"
                : category === "misc"
                ? "Upload any miscellaneous document above"
                : category === "usa"
                ? "Upload your US tax records, IDs and paperwork above"
                : isCabinet
                ? "Upload your business license and company paperwork above"
                : "Upload your first shipping document above"}
            </p>
          </div>
        ) : isCabinet ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="company-docs-grid">
            {categoryDocs.map((d, i) => renderDocCard(d, i))}
          </div>
        ) : (
          <div className="space-y-4">
            {groupKeys.map((key) => {
              const shipment = shipmentById[key];
              const groupDocs = grouped[key] || [];
              const isUnassigned = key === "_unassigned";
              const expanded = expandedGroups[key] !== false; // default expanded

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="trade-card rounded-sm overflow-hidden"
                  data-testid={`doc-group-${key}`}
                >
                  <button
                    onClick={() => toggleGroup(key)}
                    className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4 text-left">
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                        isUnassigned ? "bg-white/5" : "bg-[#D4AF37]/10"
                      }`}>
                        {isUnassigned 
                          ? <File className="w-5 h-5 text-white/40" />
                          : <Ship className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                        }
                      </div>
                      <div>
                        <h3 className="font-serif font-semibold">
                          {isUnassigned ? "Unassigned Documents" : shipment?.shipment_name}
                        </h3>
                        <p className="text-xs text-white/50 mt-0.5">
                          {groupDocs.length} document{groupDocs.length !== 1 ? "s" : ""}
                          {!isUnassigned && shipment && (shipment.bill_of_lading || shipment.container_number) && (
                            <>
                              {" · "}
                              {shipment.bill_of_lading && <span className="font-mono">B/L {shipment.bill_of_lading}</span>}
                              {shipment.bill_of_lading && shipment.container_number && " · "}
                              {shipment.container_number && <span className="font-mono">{shipment.container_number}</span>}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <ChevronDown 
                      size={18} 
                      className={`trade-gold transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>

                  <AnimatePresence>
                    {expanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <div className="px-5 pb-5 pt-2 border-t border-white/5">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {groupDocs.map((d, i) => renderDocCard(d, i))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Scan modal */}
        {scanOpen && (
          <ScanModal onClose={() => setScanOpen(false)} onPdfReady={handleScanPdf} />
        )}
        {recordOpen && (
          <AudioRecordModal onClose={() => setRecordOpen(false)} onSave={handleAudioSave} />
        )}

        {/* Preview modal */}
        {previewDoc && (
          <div
            className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm p-4"
            onClick={() => setPreviewDoc(null)}
            data-testid="preview-modal"
          >
            <div
              className="flex items-center justify-between mb-3 max-w-5xl w-full mx-auto"
              onClick={e => e.stopPropagation()}
            >
              <p className="font-semibold truncate text-sm max-w-[60%]">{previewDoc.original_name}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownload(previewDoc)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                  data-testid="preview-download-btn"
                >
                  <Download size={12} /> Download
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="p-2 text-white/60 hover:text-white transition-colors"
                  data-testid="preview-close-btn"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div
              className="flex-1 max-w-5xl w-full mx-auto trade-card rounded-sm overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {isAudio(previewDoc) ? (
                <div className="flex items-center justify-center p-10 min-h-[30vh]" data-testid="preview-audio">
                  <audio controls autoPlay src={previewUrl(previewDoc)} className="w-full" />
                </div>
              ) : isPdf(previewDoc) ? (
                <iframe
                  src={previewUrl(previewDoc)}
                  className="w-full h-full min-h-[70vh] bg-white"
                  title={`Preview of ${previewDoc.original_name}`}
                  data-testid="preview-iframe"
                />
              ) : isImage(previewDoc) ? (
                <div className="flex items-center justify-center p-4 min-h-[50vh]">
                  <img
                    src={previewUrl(previewDoc)}
                    alt={previewDoc.original_name}
                    className="max-h-[70vh] object-contain"
                    data-testid="preview-image"
                  />
                </div>
              ) : (
                <div className="p-16 text-center" data-testid="preview-unavailable">
                  <File className="w-12 h-12 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
                  <p className="text-white/50 text-sm mb-4">
                    Preview is not available for this file type.
                  </p>
                  <button
                    onClick={() => handleDownload(previewDoc)}
                    className="text-xs trade-gold hover:underline"
                  >
                    Download to view
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
